import React, { useState, useEffect } from 'react';
import styles from './Header.module.css';
import logo from '../assets/crop_transparent_clear.svg';
import SocialIcons from './SocialIcons';

type SectionName = 'home' | 'gallery' | 'contact' | 'careers';
type Theme = 'dark' | 'light';

interface HeaderProps {
  activeSection: SectionName;
  activeTheme: Theme;
}

const Header: React.FC<HeaderProps> = ({ activeSection, activeTheme }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < 50) {
        // Always show header at the top of the page
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY) {
        // Scrolling down - hide header
        setIsVisible(false);
      } else {
        // Scrolling up - show header
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  const scrollToSection = (id: string): void => {
    const element = document.getElementById(id);

    if (element) {
      // Element exists on current page - scroll to it
      const headerElement = document.querySelector(`.${styles.header}`) as HTMLElement;
      const headerOffset = headerElement ? headerElement.offsetHeight : 0;

      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - headerOffset,
        behavior: 'smooth'
      });
    } else {
      // Element doesn't exist - we're on a separate page, navigate to main page with hash
      window.location.href = `/#${id}`;
    }
  };

  const sections: SectionName[] = ['home', 'gallery', 'contact', 'careers'];

  return (
    <header className={`${styles.header} ${isVisible ? styles.visible : styles.hidden}`}>
      <div className={styles.headerContent}>
        <div className={styles.logoSection}>
          <a
            href="/#home-section"
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              scrollToSection('home-section');
            }}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <img src={logo} alt="LB Tiling Company Logo" className={styles.logo} />
          </a>
        </div>

        <nav className={styles.navSection}>
          <ul className={styles.navLinks}>
            {sections.map((section) => (
              <li key={section}>
                <a
                  href={`#${section}-section`}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    scrollToSection(`${section}-section`);
                  }}
                  className={activeSection === section ? styles.active : ''}
                >
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.socialSection}>
          <SocialIcons iconColor="#313638" hoverColor="#f09d51" />
        </div>
      </div>
    </header>
  );
};

export default Header;